import { CardProps } from '@/components/Elements/Card/interfaces';
import ListItemData from '@/components/Layout/Tags/ListItemData';
import ListItemTitle from '@/components/Layout/Tags/ListItemTitle';
import clsx from 'clsx';
import { useState } from 'react';
import { BiChevronDown } from 'react-icons/bi';
import { BsPlusCircleFill } from 'react-icons/bs';
import { FaBirthdayCake } from 'react-icons/fa';
import { SiMoleculer } from 'react-icons/si';
import { LabMatchResultPanel } from '@/features/requests/api/labMatchDrawer.api';

interface LabResultProps {
	firstName: string;
	lastName: string;
	dateOfBirth: Date;
	age: number;
	mrn?: string;
	sex?: string;
	race?: string;
	ethnicity?: string;
	labs: LabMatchResultPanel[];
	testSelector?: string;
}

type Props = LabResultProps & CardProps;

const LabResultCard = ({
  firstName,
  lastName,
  dateOfBirth,
  age,
  mrn,
  sex,
  race,
  ethnicity,
  labs,
  className,
  showBorder,
  onButtonClick,
  isButtonDisabled,
  testSelector
}: Props) => {
  return (
    <div
      className={clsx(
        'flex overflow-hidden rounded-lg shadow-lg border-t border-slate-100',
        className
      )}
      data-cy={`lab-result-card-${testSelector}`}
    >
      <div className="flex items-center px-2.5 shadow-inner bg-darkGray-500 text-orange-400">
        <span className="p-1 border border-orange-400 rounded-full">
          <SiMoleculer className="w-4 h-4" />
        </span>
      </div>
      <div
        className={clsx(
          'flex-grow rounded-lg rounded-l-none shadow-lg bg-white',
          showBorder && 'border-2 border-darkGray-500'
        )}
      >
        <LabMatchPatientDemographics
          name={`${firstName} ${lastName}`}
          dateOfBirth={dateOfBirth}
          age={age}
          mrn={mrn}
          sex={sex}
          race={race}
          ethnicity={ethnicity}
        />
        <div className="bg-slate-100">
          <LabList labs={labs} />
          <div className="flex justify-end w-full pb-3 pr-3">
            <button
              className={clsx(
                'bg-gradient-to-br text-sm py-1.5 px-2 font-semibold rounded-md shadow-md flex items-center text-zinc-100 from-orange-500 to-orange-600',
                isButtonDisabled && 'from-gray-400 to-gray-500'
              )}
              disabled={isButtonDisabled}
              onClick={onButtonClick}
              data-cy={`lab-result-card-action-button`}
            >
              <BsPlusCircleFill className="text-white mr-1.5" /> Add to patient profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface LabMatchPatientDemographicsProps {
	name: string;
	dateOfBirth: Date;
	age: number;
	mrn: string | undefined;
	race: string | undefined;
	ethnicity: string | undefined;
	sex: string | undefined;
}

const LabMatchPatientDemographics = ({
  name,
  mrn,
  race,
  ethnicity,
  dateOfBirth,
  age,
  sex
}: LabMatchPatientDemographicsProps) => (
  <div className="flex flex-wrap w-full p-3 pl-2.5">
    <div className="flex-grow">
      <h3
        className="inline-block text-xl font-light tracking-wide whitespace-nowrap"
        data-cy={`lab-result-card-name`}
      >
        {name}
      </h3>
      {mrn && (
        <span
          className="inline-block px-1 py-px ml-1.5 -mt-1.5 text-xs text-white align-middle rounded-md bg-darkGray-700"
          data-cy={`lab-result-card-mrn`}
        >
          <span className="font-semibold tracking mr-1.5">MRN</span>
          {mrn}
        </span>
      )}
      {(race || ethnicity) && (
        <p
          className="mt-0.5 text-sm tracking-tight whitespace-nowrap"
          data-cy={`lab-result-card-race-ethnicity`}
        >
          {[race, ethnicity].filter((x) => !!x).join(', ')}
        </p>
      )}
    </div>
    <div className="pl-2 mt-1 2xl:ml-auto 2xl:text-right">
      <p
        className="inline-flex items-center pl-2 ml-auto text-sm font-semibold tracking-tight"
        data-cy={`lab-result-card-dob`}
      >
        <FaBirthdayCake className="mr-1.5 text-zinc-300" />
        {dateOfBirth.toLocaleDateString()}
      </p>
      {age && sex && (
        <p className="text-sm font-semibold tracking-tight" data-cy={`lab-result-card-age-sex`}>
          {age} year old {sex}
        </p>
      )}
    </div>
  </div>
);

interface LabListProps {
	labs: LabMatchResultPanel[];
}

const LabList = ({ labs }: LabListProps) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div className="pl-2.5 px-3 py-2 flex items-center">
        <ListItemTitle className="text-sm tracking-wide">lab results ({labs.length})</ListItemTitle>
        {labs.length > 2 ? (
          <button
            className="inline-flex items-center ml-auto text-sm font-semibold text-orange-600"
            onClick={() => setExpanded((exp) => !exp)}
          >
            {expanded ? 'Collapse results' : 'Show all results'}
            <BiChevronDown
              className={clsx('text-sm transition-transform', expanded && 'rotate-180')}
            />
          </button>
        ) : null}
      </div>
      <div className={'h-auto'}>
        {labs.slice(0, expanded ? undefined : 2).map((lab, index) => (
          <LabDetailPanel
            key={lab.accessionNumber}
            lab={lab}
            hideBorder={labs.length - 1 === index || (!expanded && index === 1)}
          />
        ))}
      </div>
    </>
  );
};

interface LabDetailPanelProps {
	lab: LabMatchResultPanel;
	hideBorder: boolean;
}

const LabDetailPanel = ({ lab, hideBorder }: LabDetailPanelProps) => {
  return (
    <div className={clsx('flex pb-2.5 mx-3.5 mb-2.5 border-slate-200', !hideBorder && 'border-b')}>
      <dl className="text-sm">
        <div>
          <ListItemTitle className="inline mr-3">Panel Name</ListItemTitle>
          <ListItemData className="inline">{lab.panelName}</ListItemData>
        </div>
        <div>
          <ListItemTitle className="inline mr-3">Reporting Lab</ListItemTitle>
          <ListItemData className="inline">{lab.reportingLab}</ListItemData>
        </div>
        <div>
          <ListItemTitle className="inline mr-3">Diagnosis</ListItemTitle>
          <ListItemData className="inline">{lab.diagnosis}</ListItemData>
        </div>
      </dl>
      <dl className="pl-2.5 ml-auto text-sm">
        <div>
          <ListItemTitle className="inline mr-3">Accession #</ListItemTitle>
          <ListItemData className="inline">{lab.accessionNumber}</ListItemData>
        </div>
        <div>
          <ListItemTitle className="inline mr-3">Report Date</ListItemTitle>
          <ListItemData className="inline">
            {lab.reportDate?.toLocaleDateString() || '\u2014'}
          </ListItemData>
        </div>
        {lab.pdfUrl && (
          <div>
            <a
              href={lab.pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="underline text-sky-500"
            >
							View Full Caris Report
            </a>
          </div>
        )}
      </dl>
    </div>
  );
};

export { LabResultCard as default };
